// ============================================================================
// Document Uploader Component
// Handles document uploads with preview, validation, and I-9 compliance
// ============================================================================

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Upload,
  File,
  FileText,
  Image,
  X,
  Check,
  AlertTriangle,
  Eye,
  Download,
  Trash2,
  Camera,
  RefreshCw,
  Shield,
  Clock,
  Info,
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

// ============================================================================
// ACCEPTED FILE TYPES & LIMITS
// ============================================================================

const ACCEPTED_FILE_TYPES = {
  document: {
    accept: '.pdf,.doc,.docx',
    mimeTypes: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    label: 'PDF or Word Document',
  },
  image: {
    accept: '.jpg,.jpeg,.png,.gif,.webp',
    mimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    label: 'Image (JPG, PNG, GIF)',
  },
  any: {
    accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp',
    mimeTypes: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
    ],
    label: 'PDF, Word, or Image',
  },
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILES_DEFAULT = 5;

// ============================================================================
// FILE PREVIEW COMPONENT
// ============================================================================

function FilePreview({ file, onRemove, onView, showRemove = true }) {
  const isImage = file.type?.startsWith('image/') || file.previewUrl;
  const isPDF = file.type === 'application/pdf' || file.name?.endsWith('.pdf');

  const getFileIcon = () => {
    if (isImage) return <Image className="h-8 w-8 text-blue-500" />;
    if (isPDF) return <FileText className="h-8 w-8 text-red-500" />;
    return <File className="h-8 w-8 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border group hover:border-blue-200 transition-colors">
      {/* Preview Thumbnail */}
      <div className="flex-shrink-0 w-12 h-12 rounded bg-white border flex items-center justify-center overflow-hidden">
        {isImage && file.previewUrl ? (
          <img
            src={file.previewUrl}
            alt={file.name}
            className="w-full h-full object-cover"
          />
        ) : (
          getFileIcon()
        )}
      </div>

      {/* File Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>{formatFileSize(file.size)}</span>
          {file.uploadedAt && (
            <>
              <span>•</span>
              <span>{new Date(file.uploadedAt).toLocaleDateString()}</span>
            </>
          )}
        </div>
      </div>

      {/* Status Badge */}
      {file.status && (
        <Badge
          variant="outline"
          className={
            file.status === 'verified'
              ? 'bg-green-50 text-green-700 border-green-200'
              : file.status === 'pending'
                ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                : file.status === 'rejected'
                  ? 'bg-red-50 text-red-700 border-red-200'
                  : 'bg-gray-50'
          }
        >
          {file.status === 'verified' && <Check className="h-3 w-3 mr-1" />}
          {file.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
          {file.status === 'rejected' && <X className="h-3 w-3 mr-1" />}
          {file.status}
        </Badge>
      )}

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {onView && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onView(file)}
            className="h-8 w-8 p-0"
          >
            <Eye className="h-4 w-4" />
          </Button>
        )}
        {showRemove && onRemove && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemove(file)}
            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// FILE VIEWER DIALOG
// ============================================================================

function FileViewerDialog({ file, open, onClose }) {
  if (!file) return null;

  const isImage = file.type?.startsWith('image/') || file.previewUrl;
  const isPDF = file.type === 'application/pdf' || file.name?.endsWith('.pdf');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {file.name}
          </DialogTitle>
          <DialogDescription>
            Document preview
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-auto max-h-[70vh]">
          {isImage ? (
            <img
              src={file.previewUrl || file.dataUrl}
              alt={file.name}
              className="max-w-full h-auto mx-auto"
            />
          ) : isPDF ? (
            <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg">
              <FileText className="h-16 w-16 text-red-500 mb-4" />
              <p className="text-gray-600 mb-4">PDF Preview</p>
              {file.dataUrl && (
                <iframe
                  src={file.dataUrl}
                  className="w-full h-96 border rounded"
                  title={file.name}
                />
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg">
              <File className="h-16 w-16 text-gray-400 mb-4" />
              <p className="text-gray-600">Preview not available for this file type</p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          {file.dataUrl && (
            <Button variant="outline" asChild>
              <a href={file.dataUrl} download={file.name}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </a>
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// MAIN DOCUMENT UPLOADER COMPONENT
// ============================================================================

export default function DocumentUploader({
  documentType = 'identity', // 'identity', 'employment', 'tax', 'other'
  documentCategory = '', // For I-9: 'list_a', 'list_b', 'list_c'
  acceptedTypes = 'any',
  maxFiles = MAX_FILES_DEFAULT,
  maxFileSize = MAX_FILE_SIZE,
  required = false,
  label = 'Upload Document',
  description = 'Upload required documents',
  helpText = '',
  existingFiles = [],
  onFilesChange,
  onFileUpload,
  onFileRemove,
  disabled = false,
  showCamera = false,
  className = '',
}) {
  const [files, setFiles] = useState(existingFiles);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [viewFile, setViewFile] = useState(null);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Sync internal state when existingFiles prop changes (important for viewing uploaded files)
  useEffect(() => {
    if (existingFiles && existingFiles.length > 0) {
      setFiles(existingFiles);
    }
  }, [existingFiles]);

  const acceptConfig = ACCEPTED_FILE_TYPES[acceptedTypes] || ACCEPTED_FILE_TYPES.any;

  // Validate file
  const validateFile = useCallback((file) => {
    const errors = [];

    // Check file size
    if (file.size > maxFileSize) {
      errors.push(`File size exceeds ${maxFileSize / (1024 * 1024)}MB limit`);
    }

    // Check file type
    if (!acceptConfig.mimeTypes.includes(file.type)) {
      errors.push(`File type not accepted. Please upload ${acceptConfig.label}`);
    }

    return errors;
  }, [maxFileSize, acceptConfig]);

  // Process file
  const processFile = useCallback(async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const fileData = {
          id: uuidv4(),
          name: file.name,
          type: file.type,
          size: file.size,
          dataUrl: e.target.result,
          previewUrl: file.type.startsWith('image/') ? e.target.result : null,
          uploadedAt: new Date().toISOString(),
          documentType,
          documentCategory,
          status: 'pending',
          originalFile: file,
        };
        resolve(fileData);
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }, [documentType, documentCategory]);

  // Handle file selection
  const handleFiles = useCallback(async (selectedFiles) => {
    setError(null);

    if (files.length + selectedFiles.length > maxFiles) {
      setError(`Maximum ${maxFiles} files allowed`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const newFiles = [];
    const errors = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const validationErrors = validateFile(file);

      if (validationErrors.length > 0) {
        errors.push(`${file.name}: ${validationErrors.join(', ')}`);
        continue;
      }

      try {
        const processedFile = await processFile(file);
        newFiles.push(processedFile);
        setUploadProgress(((i + 1) / selectedFiles.length) * 100);
      } catch (err) {
        errors.push(`${file.name}: ${err.message}`);
      }
    }

    if (errors.length > 0) {
      setError(errors.join('\n'));
    }

    if (newFiles.length > 0) {
      const updatedFiles = [...files, ...newFiles];
      setFiles(updatedFiles);
      onFilesChange?.(updatedFiles);

      for (const file of newFiles) {
        await onFileUpload?.(file);
      }
    }

    setUploading(false);
    setUploadProgress(0);
  }, [files, maxFiles, validateFile, processFile, onFilesChange, onFileUpload]);

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // Handle drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  }, [disabled, handleFiles]);

  // Handle file input change
  const handleInputChange = useCallback((e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
    e.target.value = ''; // Reset input
  }, [handleFiles]);

  // Handle remove file
  const handleRemove = useCallback(async (fileToRemove) => {
    const updatedFiles = files.filter((f) => f.id !== fileToRemove.id);
    setFiles(updatedFiles);
    onFilesChange?.(updatedFiles);
    await onFileRemove?.(fileToRemove);
  }, [files, onFilesChange, onFileRemove]);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Label */}
      <div className="space-y-1">
        <Label className="flex items-center gap-2">
          {label}
          {required && <span className="text-red-500">*</span>}
        </Label>
        {description && (
          <p className="text-sm text-gray-500">{description}</p>
        )}
      </div>

      {/* Drop Zone */}
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-6 transition-all
          ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={acceptConfig.accept}
          multiple={maxFiles > 1}
          onChange={handleInputChange}
          disabled={disabled}
        />

        <div className="flex flex-col items-center justify-center text-center">
          <div className={`
            w-12 h-12 rounded-full flex items-center justify-center mb-3
            ${dragActive ? 'bg-blue-100' : 'bg-gray-100'}
          `}>
            <Upload className={`h-6 w-6 ${dragActive ? 'text-blue-600' : 'text-gray-500'}`} />
          </div>

          <p className="text-sm font-medium text-gray-700">
            {dragActive ? 'Drop files here' : 'Drag and drop files here'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            or <span className="text-blue-600">browse</span> to upload
          </p>
          <p className="text-xs text-gray-400 mt-2">
            {acceptConfig.label} • Max {maxFileSize / (1024 * 1024)}MB per file
          </p>
        </div>

        {/* Upload Progress */}
        {uploading && (
          <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center rounded-lg">
            <RefreshCw className="h-8 w-8 text-blue-600 animate-spin mb-3" />
            <p className="text-sm font-medium text-gray-700">Uploading...</p>
            <div className="w-48 mt-2">
              <Progress value={uploadProgress} className="h-2" />
            </div>
          </div>
        )}
      </div>

      {/* Camera Option */}
      {showCamera && (
        <div className="flex justify-center">
          <input
            ref={cameraInputRef}
            type="file"
            className="hidden"
            accept="image/*"
            capture="environment"
            onChange={handleInputChange}
            disabled={disabled}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => cameraInputRef.current?.click()}
            disabled={disabled}
          >
            <Camera className="h-4 w-4 mr-2" />
            Take Photo
          </Button>
        </div>
      )}

      {/* Help Text */}
      {helpText && (
        <Alert className="bg-blue-50 border-blue-100">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800 text-sm">
            {helpText}
          </AlertDescription>
        </Alert>
      )}

      {/* Error Display */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="whitespace-pre-line">{error}</AlertDescription>
        </Alert>
      )}

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm text-gray-600">
            Uploaded Files ({files.length}/{maxFiles})
          </Label>
          <div className="space-y-2">
            {files.map((file) => (
              <FilePreview
                key={file.id}
                file={file}
                onRemove={!disabled ? handleRemove : null}
                onView={setViewFile}
                showRemove={!disabled}
              />
            ))}
          </div>
        </div>
      )}

      {/* File Viewer Dialog */}
      <FileViewerDialog
        file={viewFile}
        open={!!viewFile}
        onClose={() => setViewFile(null)}
      />
    </div>
  );
}

// ============================================================================
// I-9 DOCUMENT UPLOADER (Specialized for I-9)
// ============================================================================

export function I9DocumentUploader({
  listType, // 'A', 'B', 'C'
  documentType,
  documentTitle,
  expirationDate,
  onUpload,
  onRemove,
  existingDocument,
  disabled = false,
}) {
  const listLabels = {
    A: 'List A - Identity and Employment Authorization',
    B: 'List B - Identity Only',
    C: 'List C - Employment Authorization Only',
  };

  const listHelp = {
    A: 'Documents that establish both identity and employment authorization (e.g., US Passport, Permanent Resident Card)',
    B: 'Documents that establish identity only. Must be combined with a List C document.',
    C: 'Documents that establish employment authorization only. Must be combined with a List B document.',
  };

  return (
    <Card className="border-2">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            {listLabels[listType]}
          </CardTitle>
          <Badge variant="outline">{documentType}</Badge>
        </div>
        <CardDescription>{documentTitle}</CardDescription>
      </CardHeader>

      <CardContent>
        <DocumentUploader
          documentType="identity"
          documentCategory={`list_${listType.toLowerCase()}`}
          acceptedTypes="any"
          maxFiles={2}
          required
          label={`Upload ${documentTitle}`}
          description="Upload a clear photo or scan of your document"
          helpText={listHelp[listType]}
          existingFiles={existingDocument ? [existingDocument] : []}
          onFileUpload={onUpload}
          onFileRemove={onRemove}
          showCamera
          disabled={disabled}
        />

        {expirationDate && (
          <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-100">
            <div className="flex items-center gap-2 text-yellow-800">
              <Clock className="h-4 w-4" />
              <span className="text-sm font-medium">Document Expiration</span>
            </div>
            <p className="text-sm text-yellow-700 mt-1">
              Expiration Date: {new Date(expirationDate).toLocaleDateString()}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// DOCUMENT STATUS BADGE
// ============================================================================

export function DocumentStatusBadge({ status }) {
  const statusConfig = {
    pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock, label: 'Pending Review' },
    verified: { color: 'bg-green-100 text-green-800 border-green-200', icon: Check, label: 'Verified' },
    rejected: { color: 'bg-red-100 text-red-800 border-red-200', icon: X, label: 'Rejected' },
    expired: { color: 'bg-orange-100 text-orange-800 border-orange-200', icon: AlertTriangle, label: 'Expired' },
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <Badge variant="outline" className={config.color}>
      <Icon className="h-3 w-3 mr-1" />
      {config.label}
    </Badge>
  );
}
